import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Check, Clock, Star } from "lucide-react";
import { cn } from "@/lib/utils";

/**
 * Animated phone mockup that cycles through a 3-step booking flow.
 * Inspired by v0 design; adapted to the SlotBook violet light theme.
 */

const services = [
  { name: "Signature Cut", price: "$85", time: "45 min" },
  { name: "Balayage & Gloss", price: "$220", time: "2h 30m" },
  { name: "Beard Sculpt", price: "$40", time: "30 min" },
];

const times = ["9:30", "10:15", "11:00", "1:30", "2:15", "3:00"];

export default function PhoneMockup() {
  const [step, setStep] = useState(0);
  const [selectedService, setSelectedService] = useState(0);
  const [selectedTime, setSelectedTime] = useState(2);

  useEffect(() => {
    // Cycle 0 → 1 → 2 → 0 ... at a steady pace.
    // Single interval keeps the animation predictable in StrictMode.
    const id = setInterval(() => {
      setStep((s) => (s + 1) % 3);
    }, 2400);
    return () => clearInterval(id);
  }, []);

  return (
    <motion.div
      className="relative"
      initial={{ opacity: 0, y: 60, scale: 0.9, rotateY: -8 }}
      animate={{ opacity: 1, y: 0, scale: 1, rotateY: 0 }}
      transition={{ duration: 1.1, delay: 0.35, ease: [0.22, 1, 0.36, 1] }}
    >
      <motion.div
        className="relative"
        animate={{ y: [0, -22, 0], rotate: [0, 0.6, 0] }}
        transition={{
          duration: 5.5,
          repeat: Infinity,
          ease: "easeInOut",
        }}
      >
        {/* outer glow */}
        <div
          aria-hidden="true"
          className="absolute -inset-10 -z-10 rounded-full bg-gradient-to-br from-violet-500/40 via-fuchsia-400/25 to-cyan-400/30 blur-3xl"
        />

        <div
          className="relative mx-auto w-[270px] sm:w-[290px] rounded-[2.75rem] border border-slate-800 bg-slate-950 p-3 shadow-2xl"
          style={{
            boxShadow:
              "0 30px 90px -20px rgba(167,139,250,0.5), 0 24px 60px -24px rgba(0,0,0,0.65), 0 0 0 1px rgba(255,255,255,0.04)",
          }}
        >
          {/* notch removed per user request */}

          <div className="relative h-[600px] overflow-hidden rounded-[2.1rem] bg-white">
            {/* status bar */}
            <div className="flex items-center justify-between px-6 pb-2 pt-4 text-[11px] text-slate-400">
              <span>9:41</span>
              <span className="font-semibold text-slate-700">SlotBook</span>
              <span>100%</span>
            </div>

            {/* salon header */}
            <div className="px-5 pb-4">
              <div className="flex items-center gap-3 rounded-2xl bg-slate-50 p-3">
                <div className="flex size-11 items-center justify-center rounded-xl bg-gradient-to-br from-violet-600 to-indigo-600 text-base font-semibold text-white font-heading shadow-md shadow-violet-500/30">
                  LV
                </div>
                <div className="min-w-0">
                  <p className="truncate text-sm font-semibold text-slate-900">
                    Lumière Salon
                  </p>
                  <div className="flex items-center gap-1 text-[11px] text-slate-500">
                    <Star className="size-3 fill-amber-400 text-amber-400" />
                    <span className="text-slate-700 font-medium">4.9</span>
                    <span>· Surry Hills, Sydney</span>
                  </div>
                </div>
              </div>
            </div>

            {/* progress */}
            <div className="flex items-center gap-1.5 px-5 pb-4">
              {[0, 1, 2].map((s) => (
                <div
                  key={s}
                  className={cn(
                    "h-1 flex-1 rounded-full transition-colors duration-500",
                    s <= step
                      ? "bg-gradient-to-r from-violet-600 to-indigo-600"
                      : "bg-slate-200",
                  )}
                />
              ))}
            </div>

            {/* steps container */}
            <div className="relative px-5">
              {/* Step 0: pick service */}
              <div
                className={cn(
                  "transition-all duration-500",
                  step === 0
                    ? "opacity-100"
                    : "pointer-events-none absolute inset-x-5 opacity-0",
                )}
              >
                <p className="mb-3 text-[10px] font-semibold uppercase tracking-wider text-slate-400">
                  Choose a service
                </p>
                <div className="flex flex-col gap-2.5">
                  {services.map((svc, i) => (
                    <button
                      type="button"
                      key={svc.name}
                      onClick={() => setSelectedService(i)}
                      className={cn(
                        "flex items-center justify-between rounded-xl border p-3 text-left transition-all",
                        selectedService === i
                          ? "border-violet-300 bg-violet-50"
                          : "border-slate-200 bg-white",
                      )}
                    >
                      <div>
                        <p className="text-sm font-semibold text-slate-900">
                          {svc.name}
                        </p>
                        <div className="mt-0.5 flex items-center gap-1 text-[11px] text-slate-500">
                          <Clock className="size-3" /> {svc.time}
                        </div>
                      </div>
                      <span className="text-sm font-bold text-violet-600">
                        {svc.price}
                      </span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Step 1: pick time */}
              <div
                className={cn(
                  "transition-all duration-500",
                  step === 1
                    ? "opacity-100"
                    : "pointer-events-none absolute inset-x-5 opacity-0",
                )}
              >
                <p className="mb-3 text-[10px] font-semibold uppercase tracking-wider text-slate-400">
                  Tuesday, 14 May
                </p>
                <div className="grid grid-cols-3 gap-2.5">
                  {times.map((t, i) => (
                    <button
                      type="button"
                      key={t}
                      onClick={() => setSelectedTime(i)}
                      className={cn(
                        "rounded-xl border py-3 text-sm font-semibold transition-all",
                        selectedTime === i
                          ? "border-violet-600 bg-gradient-to-br from-violet-600 to-indigo-600 text-white shadow-md shadow-violet-500/30"
                          : "border-slate-200 bg-white text-slate-700",
                      )}
                    >
                      {t}
                    </button>
                  ))}
                </div>
                <div className="mt-4 rounded-xl bg-slate-50 p-3 text-xs text-slate-500">
                  <span className="text-slate-900 font-medium">
                    {services[selectedService].name}
                  </span>{" "}
                  with Amara · {services[selectedService].time}
                </div>
              </div>

              {/* Step 2: confirmed */}
              <div
                className={cn(
                  "flex flex-col items-center justify-center pt-8 text-center transition-all duration-500",
                  step === 2
                    ? "opacity-100"
                    : "pointer-events-none absolute inset-x-5 opacity-0",
                )}
              >
                <div className="relative mb-5 flex size-20 items-center justify-center">
                  <span className="absolute inset-0 rounded-full bg-violet-500/30 [animation:pulse-ring_1.8s_ease-out_infinite]" />
                  <span className="flex size-20 items-center justify-center rounded-full bg-gradient-to-br from-violet-600 to-indigo-600 text-white shadow-lg shadow-violet-500/40">
                    <Check className="size-9" strokeWidth={2.5} />
                  </span>
                </div>
                <p className="font-heading text-2xl font-bold text-slate-900">
                  Booking confirmed
                </p>
                <p className="mt-1 px-4 text-xs text-slate-500">
                  A confirmation and reminder have been sent to your phone.
                </p>
                <div className="mt-5 w-full rounded-xl bg-slate-50 p-4 text-left">
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-500">Service:</span>
                    <span className="text-slate-900 font-medium">
                      {services[selectedService].name}
                    </span>
                  </div>
                  <div className="mt-2 flex justify-between text-sm">
                    <span className="text-slate-500">When:</span>
                    <span className="text-slate-900 font-medium">
                      Tue 14 May · {times[selectedTime]}
                    </span>
                  </div>
                  <div className="mt-2 flex justify-between text-sm">
                    <span className="text-slate-500">Deposit</span>
                    <span className="text-violet-600 font-semibold">
                      Paid · {services[selectedService].price}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* CTA pinned */}
            <div className="absolute inset-x-5 bottom-6">
              <div className="flex h-12 items-center justify-center rounded-full bg-gradient-to-r from-violet-600 to-indigo-600 text-sm font-semibold text-white shadow-lg shadow-violet-500/30">
                {step === 0 && "Continue"}
                {step === 1 && "Confirm booking"}
                {step === 2 && "Add to calendar"}
              </div>
            </div>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
}
